import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MessageSquare, 
  Send, 
  Bot, 
  User, 
  Trash2, 
  Plus,
  Lightbulb,
  Users,
  Loader2,
  Copy,
  Check,
  History
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

export const AIAssistantPage = () => {
  const [activeTab, setActiveTab] = useState('coaching');
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await api.get('/ai/conversations');
      setConversations(response.data);
    } catch (error) {
      // Silent fail - conversations list will be empty
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const loadConversation = async (convId) => {
    try {
      const response = await api.get(`/ai/conversations/${convId}`);
      setMessages(response.data.messages || []);
      setConversationId(convId);
      setActiveTab(response.data.mode || 'coaching');
      setShowHistory(false);
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/ai/conversations/${convId}`);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (conversationId === convId) {
        startNewChat();
      }
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setShowHistory(false);
  };

  const sendMessage = async () => {
    if (!message.trim() || isStreaming) return;

    const userMessage = { role: 'user', content: message, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    const currentMessage = message;
    setMessage('');
    setIsStreaming(true);

    // Add placeholder for assistant message
    setMessages(prev => [...prev, { role: 'assistant', content: '', timestamp: new Date().toISOString(), loading: true }]);

    try {
      const response = await api.post('/ai/chat', {
        message: currentMessage,
        mode: activeTab,
        conversation_id: conversationId
      });

      const { response: aiResponse, conversation_id: newConvId } = response.data;
      
      // Update the last message with actual response
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'assistant',
          content: aiResponse,
          timestamp: new Date().toISOString()
        };
        return updated;
      });

      setConversationId(newConvId);
      fetchConversations();
    } catch (error) {
      // console.error('AI chat error:', error);
      toast.error(error.response?.data?.detail || 'Failed to get AI response');
      // Remove the empty assistant message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsStreaming(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
    toast.success('Copied to clipboard');
  };

  const suggestedPrompts = {
    coaching: [
      "How can I hit my Sprint to District goal this month?",
      "What's the best way to handle rejection in cold calling?",
      "Give me a morning routine for maximum productivity",
      "How do I build a team that stays motivated?"
    ],
    prospect: [
      "Help me write an opening message for a warm market contact",
      "What should I say to re-engage a prospect who went cold?",
      "How do I transition from small talk to business?",
      "Give me follow-up templates for after a presentation"
    ]
  };

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="ai-assistant-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#333333]">AI Assistant</h1>
          <p className="text-gray-600 text-sm">Get coaching advice and prospect conversation suggestions</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2"
            data-testid="history-button"
          >
            <History className="h-4 w-4" />
            History
          </Button>
          <Button
            onClick={startNewChat}
            className="bg-[#E31837] hover:bg-[#C4122C] flex items-center gap-2"
            data-testid="new-chat-button"
          >
            <Plus className="h-4 w-4" />
            New Chat
          </Button>
        </div>
      </div>

      <div className="flex-1 flex gap-4 min-h-0">
        {/* History Sidebar */}
        {showHistory && (
          <Card className="w-72 p-4 flex flex-col" data-testid="history-sidebar">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <History className="h-4 w-4" />
              Conversation History
            </h3>
            <ScrollArea className="flex-1">
              <div className="space-y-2">
                {conversations.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No conversations yet</p>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => loadConversation(conv.id)}
                      className={`p-3 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors ${
                        conversationId === conv.id ? 'bg-gray-100 border-l-2 border-[#E31837]' : ''
                      }`}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-200 capitalize">
                          {conv.mode}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => deleteConversation(conv.id, e)}
                          className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <p className="text-sm text-gray-700 mt-1 truncate">
                        {conv.messages?.[0]?.content || 'Empty conversation'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(conv.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </Card>
        )}

        {/* Main Chat Area */}
        <Card className="flex-1 flex flex-col p-4 min-h-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
            <TabsList className="mb-4">
              <TabsTrigger value="coaching" className="flex items-center gap-2" data-testid="coaching-tab">
                <Lightbulb className="h-4 w-4" />
                Coaching & Advice
              </TabsTrigger>
              <TabsTrigger value="prospect" className="flex items-center gap-2" data-testid="prospect-tab">
                <Users className="h-4 w-4" />
                Prospect Suggestions
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="flex-1 flex flex-col min-h-0 mt-0">
              {/* Messages Area */}
              <ScrollArea className="flex-1 pr-4">
                {messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center py-12">
                    <Bot className="h-16 w-16 text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 mb-2">
                      {activeTab === 'coaching' ? 'Your AI Coach' : 'Prospect Conversation Helper'}
                    </h3>
                    <p className="text-gray-500 text-center max-w-md mb-6">
                      {activeTab === 'coaching'
                        ? 'Get personalized advice on sales, prospecting, team building, and achieving your goals.'
                        : 'Get message suggestions and conversation strategies for your prospects.'}
                    </p>
                    
                    {/* Suggested Prompts */}
                    <div className="w-full max-w-lg">
                      <p className="text-sm text-gray-500 mb-3 text-center">Try asking:</p>
                      <div className="grid grid-cols-1 gap-2">
                        {suggestedPrompts[activeTab].map((prompt, idx) => (
                          <button
                            key={idx}
                            onClick={() => setMessage(prompt)}
                            className="text-left p-3 rounded-lg border hover:border-[#E31837] hover:bg-red-50 transition-colors text-sm"
                            data-testid={`suggested-prompt-${idx}`}
                          >
                            {prompt}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 py-4">
                    {messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                        data-testid={`message-${idx}`}
                      >
                        {msg.role === 'assistant' && (
                          <div className="h-8 w-8 rounded-full bg-[#E31837] flex items-center justify-center flex-shrink-0">
                            <Bot className="h-5 w-5 text-white" />
                          </div>
                        )}
                        <div
                          className={`max-w-[80%] rounded-lg p-3 ${
                            msg.role === 'user'
                              ? 'bg-[#E31837] text-white'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                          {msg.role === 'assistant' && msg.content && (
                            <button
                              onClick={() => copyToClipboard(msg.content, idx)}
                              className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                            >
                              {copiedIndex === idx ? (
                                <><Check className="h-3 w-3" /> Copied</>
                              ) : (
                                <><Copy className="h-3 w-3" /> Copy</>
                              )}
                            </button>
                          )}
                        </div>
                        {msg.role === 'user' && (
                          <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                            <User className="h-5 w-5 text-gray-600" />
                          </div>
                        )}
                      </div>
                    ))}
                    {isStreaming && messages[messages.length - 1]?.loading && (
                      <div className="flex gap-3">
                        <div className="h-8 w-8 rounded-full bg-[#E31837] flex items-center justify-center">
                          <Bot className="h-5 w-5 text-white" />
                        </div>
                        <div className="bg-gray-100 rounded-lg p-3 flex items-center gap-2">
                          <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
                          <span className="text-sm text-gray-500">Thinking...</span>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Input Area */}
              <div className="mt-4 border-t pt-4">
                <div className="flex gap-2">
                  <Textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      activeTab === 'coaching'
                        ? 'Ask for coaching advice...'
                        : 'Describe your prospect or ask for message suggestions...'
                    }
                    className="flex-1 min-h-[60px] max-h-[120px] resize-none"
                    disabled={isStreaming}
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!message.trim() || isStreaming}
                    className="bg-[#E31837] hover:bg-[#C4122C] h-auto px-4"
                    data-testid="send-button"
                  >
                    {isStreaming ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Send className="h-5 w-5" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Press Enter to send, Shift+Enter for new line
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};
